import { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft,
  Play,
  Pause,
  Eye,
  Calendar,
  User,
  Building,
  ChevronLeft,
  ChevronRight,
  FileText,
  Volume2,
  VolumeX,
  Maximize,
  SkipBack,
  SkipForward,
  Loader2,
  Share2,
  Zap
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Slider } from "../components/ui/slider";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function UserLibraryDetail() {
  const { token, id } = useParams();
  const videoRef = useRef(null);
  
  const [session, setSession] = useState(null);
  const [slides, setSlides] = useState([]);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [hasVideo, setHasVideo] = useState(false);
  const [videoUrl, setVideoUrl] = useState(null);

  useEffect(() => {
    fetchSessionData();
    trackView();
  }, [id]);

  const fetchSessionData = async () => {
    setLoading(true);
    try {
      // Fetch session
      const sessionRes = await axios.get(`${API}/sessions/${id}`);
      setSession(sessionRes.data);
      
      // Fetch slides
      const slidesRes = await axios.get(`${API}/sessions/${id}/slides`);
      setSlides(slidesRes.data.slides || []);
      
      // Fetch user data
      const userRes = await axios.get(`${API}/users/${sessionRes.data.user_id}`);
      setUserData(userRes.data);
      
      // Check for video
      try {
        const replayRes = await axios.get(`${API}/sessions/${id}/replay`);
        if (replayRes.data.has_video) {
          setHasVideo(true);
          setVideoUrl(replayRes.data.video_url);
        }
      } catch (e) {
        // No video available
      }
    } catch (error) {
      console.error("Failed to fetch session data:", error);
      toast.error("Failed to load presentation");
    } finally {
      setLoading(false);
    }
  };

  const trackView = async () => {
    try {
      await axios.post(`${API}/library/presentations/${id}/view`);
    } catch (error) {
      console.error("Failed to track view:", error);
    }
  };

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
    }
  };

  const handleSeek = (value) => {
    if (videoRef.current) {
      videoRef.current.currentTime = value[0];
      setCurrentTime(value[0]);
    }
  };

  const handleVolumeChange = (value) => {
    const newVolume = value[0];
    setVolume(newVolume);
    if (videoRef.current) {
      videoRef.current.volume = newVolume;
    }
    setIsMuted(newVolume === 0);
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const skip = (seconds) => {
    if (videoRef.current) {
      videoRef.current.currentTime += seconds;
    }
  };

  const toggleFullscreen = () => {
    if (videoRef.current) {
      if (document.fullscreenElement) {
        document.exitFullscreen();
      } else {
        videoRef.current.requestFullscreen();
      }
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const nextSlide = () => {
    if (currentSlide < slides.length - 1) {
      setCurrentSlide(currentSlide + 1);
    }
  };

  const prevSlide = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  const copyShareLink = () => {
    const url = `${window.location.origin}/u/${token}/library/${id}`;
    navigator.clipboard.writeText(url);
    toast.success("Link copied to clipboard!");
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!session) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="text-center py-12">
          <p className="text-slate-600 mb-4">Presentation not found</p>
          <Link to={`/u/${token}/library`}>
            <Button>Back to Library</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-slate-200/50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to={`/u/${token}/library`}>
              <Button variant="ghost" size="icon" className="rounded-full">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </Link>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center">
                <Zap className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>EVOLVE</span>
            </div>
          </div>
          <Button variant="outline" onClick={copyShareLink} className="rounded-full">
            <Share2 className="w-4 h-4 mr-2" />
            Share
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Title Section */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
            {session.topic_name}
          </h1>
          <div className="flex items-center gap-4 mt-2 text-sm text-slate-600">
            <span className="flex items-center gap-1">
              <User className="w-4 h-4" />
              {userData?.name || 'Unknown'}
            </span>
            <span className="flex items-center gap-1">
              <Building className="w-4 h-4" />
              {userData?.department || 'N/A'}
            </span>
            <span className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              {formatDate(session.created_at)}
            </span>
            <span className="flex items-center gap-1">
              <Eye className="w-4 h-4" />
              {session.view_count || 0} views
            </span>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Video/Slides Area */}
          <div className="lg:col-span-2 space-y-4">
            <Card className="overflow-hidden border-0 shadow-xl">
              <CardContent className="p-0">
                {hasVideo ? (
                  <div className="relative bg-black">
                    <video
                      ref={videoRef}
                      src={`${process.env.REACT_APP_BACKEND_URL}${videoUrl}`}
                      className="w-full aspect-video"
                      onTimeUpdate={handleTimeUpdate}
                      onLoadedMetadata={handleLoadedMetadata}
                      onEnded={() => setIsPlaying(false)}
                    />
                    
                    {/* Video Controls */}
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                      <Slider
                        value={[currentTime]}
                        max={duration || 100}
                        step={0.1}
                        onValueChange={handleSeek}
                        className="mb-4"
                      />
                      
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => skip(-10)}
                            className="text-white hover:bg-white/20"
                          >
                            <SkipBack className="w-5 h-5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={togglePlay}
                            className="text-white hover:bg-white/20"
                          >
                            {isPlaying ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6" />}
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => skip(10)}
                            className="text-white hover:bg-white/20"
                          >
                            <SkipForward className="w-5 h-5" />
                          </Button>
                          <span className="text-white text-sm font-mono ml-2">
                            {formatTime(currentTime)} / {formatTime(duration)}
                          </span>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={toggleMute}
                            className="text-white hover:bg-white/20"
                          >
                            {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                          </Button>
                          <div className="w-24">
                            <Slider
                              value={[isMuted ? 0 : volume]}
                              max={1}
                              step={0.1}
                              onValueChange={handleVolumeChange}
                            />
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={toggleFullscreen}
                            className="text-white hover:bg-white/20"
                          >
                            <Maximize className="w-5 h-5" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : slides.length > 0 ? (
                  <div className="relative">
                    {/* Main Slide */}
                    <div className="aspect-video bg-slate-100 flex items-center justify-center">
                      <img
                        src={`${process.env.REACT_APP_BACKEND_URL}${slides[currentSlide]}`}
                        alt={`Slide ${currentSlide + 1}`}
                        className="max-w-full max-h-full object-contain"
                      />
                    </div>
                    
                    {/* Slide Navigation */}
                    <div className="absolute inset-y-0 left-0 flex items-center">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={prevSlide}
                        disabled={currentSlide === 0}
                        className="ml-2 bg-white/80 hover:bg-white rounded-full shadow-lg"
                      >
                        <ChevronLeft className="w-6 h-6" />
                      </Button>
                    </div>
                    <div className="absolute inset-y-0 right-0 flex items-center">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={nextSlide}
                        disabled={currentSlide === slides.length - 1}
                        className="mr-2 bg-white/80 hover:bg-white rounded-full shadow-lg"
                      >
                        <ChevronRight className="w-6 h-6" />
                      </Button>
                    </div>
                    
                    {/* Slide Counter */}
                    <div className="absolute bottom-4 right-4 px-3 py-1.5 bg-black/60 text-white rounded-full text-sm font-medium">
                      {currentSlide + 1} / {slides.length}
                    </div>
                  </div>
                ) : (
                  <div className="aspect-video bg-slate-100 flex flex-col items-center justify-center">
                    <FileText className="w-16 h-16 text-slate-300 mb-4" />
                    <p className="text-slate-500">No slides available</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Concept Overview */}
            <Card className="border-0 shadow-lg">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">Overview</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600">{session.concept_overview || 'No overview provided.'}</p>
                {session.notes && (
                  <div className="mt-4 pt-4 border-t">
                    <h4 className="font-medium text-slate-900 mb-2">Notes</h4>
                    <p className="text-slate-600 text-sm">{session.notes}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar - Slide Thumbnails */}
          <div className="space-y-4">
            <Card className="border-0 shadow-lg">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <FileText className="w-5 h-5 text-primary" />
                  Slides ({slides.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {slides.length > 0 ? (
                  <div className="grid grid-cols-2 gap-3 max-h-[500px] overflow-y-auto pr-2">
                    {slides.map((slide, idx) => (
                      <button
                        key={idx}
                        onClick={() => setCurrentSlide(idx)}
                        className={`relative aspect-video rounded-lg overflow-hidden border-2 transition-all ${
                          idx === currentSlide 
                            ? 'border-primary ring-2 ring-primary/20' 
                            : 'border-slate-200 hover:border-primary/50'
                        }`}
                      >
                        <img
                          src={`${process.env.REACT_APP_BACKEND_URL}${slide}`}
                          alt={`Slide ${idx + 1}`}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute bottom-1 right-1 bg-black/60 text-white text-xs px-1.5 py-0.5 rounded">
                          {idx + 1}
                        </div>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <FileText className="w-12 h-12 text-slate-300 mx-auto mb-2" />
                    <p className="text-sm text-slate-500">No slides available</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Presenter Info */}
            {userData && (
              <Card className="border-0 shadow-lg">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg">Presenter</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                      <User className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">{userData.name}</p>
                      <p className="text-sm text-slate-600">{userData.department}</p>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t grid grid-cols-2 gap-4 text-center">
                    <div>
                      <p className="text-2xl font-bold text-primary">{userData.sessions_completed || 0}</p>
                      <p className="text-xs text-slate-500">Sessions</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-primary">{userData.average_score || 0}</p>
                      <p className="text-xs text-slate-500">Avg Score</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
